import { apiFetch } from '../client';
import type { components } from '../generated/public.gen';

/**
 * Endpoints de AUTOREGISTRO (self-register) del rbac — Public API, SIN auth (igual que /auth/*
 * y /activate, NO la cookie backoffice). Flujo de 3 pasos:
 *   1. start      → valida tenant+rol+scope, encola el OTP por email. 200 vacío.
 *   2. verifyOtp  → verifica el OTP y emite un `completionToken` one-time.
 *   3. complete   → provisiona el usuario ACTIVE usando el completionToken.
 *
 * Tipos de REQUEST: generados de `public.gen.ts` (SelfRegisterStartRequest / *VerifyOtpRequest /
 * *CompleteRequest). NO inventados.
 *
 * NOTA DE CONTRATO (deuda): el OpenAPI tipa la respuesta de los 3 como `Ok` →
 * `Record<string, never>` (objeto vacío), pero el docstring de la ruta verify-otp dice que
 * "emite el completionToken one-time" en el body. El backend devuelve el token dentro del
 * `data` del envelope Fiado; el `.gen` no lo refleja todavía. Hasta que el backend publique
 * el shape real, tipamos la respuesta de verifyOtp localmente acá (VerifyOtpResult). Cuando el
 * OpenAPI exponga el campo, reemplazar por el tipo generado y borrar este tipo local.
 */
type StartRequest = components['schemas']['SelfRegisterStartRequest'];
type VerifyOtpRequest = components['schemas']['SelfRegisterVerifyOtpRequest'];
type CompleteRequest = components['schemas']['SelfRegisterCompleteRequest'];

/** Shape local de la respuesta de verify-otp (ver NOTA DE CONTRATO arriba). */
export interface VerifyOtpResult {
    completionToken: string;
}

/**
 * Reenvío de OTP del autoregistro (DEC-RBAC-054). Re-llama `_publishOtp(tenantId, emailLc, ref)`
 * (messages-business, NO Cognito) tras validar un `pending` existente; misma postura anti-enum
 * del `start` (200 genérico). Solo necesita identificar el `pending` → `{ tenantId, email }`.
 *
 * TODO: reemplazar por tipo generado tras regen openapi — el backend de /self-register/resend-otp
 * aún NO está desplegado; tipado contra el contrato del spec §4.
 */
export interface ResendSelfRegisterOtpRequestLocal {
    tenantId: string;
    // DEC-RBAC-097: identificador dual (email o teléfono). `email` = puente al backend (lee identifier ?? email).
    identifier: string;
    email: string;
}

export const selfRegisterApi = {
    start: (body: StartRequest) =>
        apiFetch<Record<string, never>>('/self-register/start', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    verifyOtp: (body: VerifyOtpRequest) =>
        apiFetch<VerifyOtpResult>('/self-register/verify-otp', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    complete: (body: CompleteRequest) =>
        apiFetch<Record<string, never>>('/self-register/complete', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    /**
     * Reenvío de OTP del autoregistro (DEC-RBAC-054). Re-encola el código por email validando un
     * `pending` existente; 200 genérico anti-enum. Ver ResendSelfRegisterOtpRequestLocal.
     */
    resendOtp: (body: ResendSelfRegisterOtpRequestLocal) =>
        apiFetch<Record<string, never>>('/self-register/resend-otp', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
};
